import { useQuery, useMutation, useQueryClient } from "react-query";
import { useEffect } from "react";
import {
  fetchData,
  fetchPublicSurvey,
  startPublicSurveySession,
  submitPublicSurveyAnswers,
  listPartnerSurveys,
  listPlatformSurveys,
  fetchPartnerSurveyAnalyticsDeferred,
  fetchPlatformSurveyAnalyticsDeferred,
  fetchDashboardDeferred,
} from "../api/client";
import { useConsolidatedDashboardContext } from "./useConsolidatedDashboardContext";
import { useDashboardJobBootstrap } from "../context/DashboardJobBootstrapContext";
import { isDeferredDashboardMeta } from "./dashboardDeferredPoll";
import {
  shouldPrefetchDashboardPreview,
  shouldPrefetchPartnerDashboardPreview,
} from "../utils/dashboardDateRange";
import { bootstrapSurveyJob, bootstrapPartnerSurveyJob } from "../utils/dashboardJobBootstrap";
import {
  useDashboardBackgroundJobs,
  useDashboardJobsRegister,
} from "../context/DashboardJobsContext";
import { dashboardRouteLabel } from "../utils/dashboardJobLabels";
import { buildDashboardScopeKey } from "../utils/dashboardScopeKey";

async function fetchPartnerSurveyAnalyticsPreview(partnerSurveyKey, filters) {
  const previewRes = await fetchPartnerSurveyAnalyticsDeferred(partnerSurveyKey, {
    ...filters,
    preview: 1,
  });
  if (previewRes.httpStatus === 200 && previewRes.data) {
    return previewRes.data;
  }
  return null;
}

async function fetchSurveyAnalyticsPreview(surveyId, filters) {
  const params = new URLSearchParams();
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.sourceLabel) params.set("sourceLabel", filters.sourceLabel);
  if (filters.channel) params.set("channel", filters.channel);
  if (filters.originId) params.set("originId", String(filters.originId));
  params.set("preview", "1");
  const queryStr = params.toString();
  const url = `/surveys/${surveyId}/analytics${queryStr ? `?${queryStr}` : ""}`;
  const previewRes = await fetchDashboardDeferred(url);
  if (previewRes.httpStatus === 200 && previewRes.data) {
    return previewRes.data;
  }
  return null;
}

export const useSurveys = (page = 1, limit = 20, search = "", status = "", options = {}) => {
  const queryClient = useQueryClient();
  const consolidatedCtx = useConsolidatedDashboardContext();
  const consolidatedSurveys = Boolean(consolidatedCtx?.surveysEnabled);
  const isPlatform = consolidatedCtx?.variant === "platform";
  const surveysPrefix = isPlatform ? "platform-surveys" : "partner-surveys";

  const query = useQuery(
    consolidatedSurveys
      ? [surveysPrefix, page, limit, search, status, consolidatedCtx.dominioIds]
      : ["surveys", page, limit, search, status],
    () => {
      if (consolidatedSurveys) {
        const listFn = isPlatform ? listPlatformSurveys : listPartnerSurveys;
        return listFn({
          page,
          limit,
          search,
          status,
          dominio_ids: consolidatedCtx.dominioIds,
        }).then((payload) => ({
          data: payload?.data || [],
          pagination: payload?.pagination || { total: 0, page: 1, totalPages: 1 },
        }));
      }
      return fetchData(
        `/surveys?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}${
          status ? `&status=${encodeURIComponent(status)}` : ""
        }`
      );
    },
    {
      staleTime: 5000,
      refetchOnWindowFocus: false,
      enabled: options.enabled !== false,
      select: (response) => ({
        surveys: response.data || [],
        pagination: response.pagination || { total: 0, page: 1, totalPages: 1 },
      }),
      onError: (err) => {
        console.error("Erro ao carregar pesquisas:", err.message);
      },
    }
  );

  const saveMutation = useMutation(
    (survey) => {
      const endpoint = survey.id ? `/surveys/${survey.id}` : `/surveys`;
      const method = survey.id ? "PUT" : "POST";
      return fetchData(endpoint, method, survey);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("surveys");
        queryClient.invalidateQueries("surveys-summary");
      },
      onError: (error) => {
        console.error(
          "Erro ao salvar pesquisa:",
          error.response?.data?.message || error.message
        );
      },
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, status }) => fetchData(`/surveys/${id}/status`, "PATCH", { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("surveys");
        queryClient.invalidateQueries("surveys-summary");
        queryClient.invalidateQueries("survey-details");
      },
      onError: (error) => {
        console.error(
          "Erro ao atualizar status da pesquisa:",
          error.response?.data?.message || error.message
        );
      },
    }
  );

  const deleteMutation = useMutation((id) => fetchData(`/surveys/${id}`, "DELETE"), {
    onSuccess: () => {
      queryClient.invalidateQueries("surveys");
      queryClient.invalidateQueries("surveys-summary");
    },
    onError: (err) => {
      console.error("Erro ao excluir pesquisa:", err.message);
    },
  });

  const createFromTemplateMutation = useMutation(
    (payload) => fetchData("/surveys/from-template", "POST", payload),
    {
      onSuccess: () => queryClient.invalidateQueries("surveys"),
    }
  );

  const syncLegacyMutation = useMutation(
    (payload = {}) =>
      fetchData("/surveys/legacy-sync", "POST", payload, { timeout: 120000 }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("surveys");
        queryClient.invalidateQueries("surveys-summary");
        queryClient.invalidateQueries("survey-analytics");
        queryClient.invalidateQueries("survey-details");
        queryClient.invalidateQueries("survey-responses");
      },
    }
  );

  return {
    ...query,
    saveMutation,
    updateStatusMutation,
    deleteMutation,
    createFromTemplateMutation,
    syncLegacyMutation,
  };
};

export const useWorkspaceSurveySummary = (periodFilters = null) => {
  const period = periodFilters || {};
  const startDate = period.startDate || "";
  const endDate = period.endDate || "";

  return useQuery(
    ["surveys-summary", startDate, endDate],
    () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const query = params.toString();
      return fetchData(`/surveys/summary${query ? `?${query}` : ""}`, "GET");
    },
    {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      select: (response) => response.data || null,
    }
  );
};

export const useSurveyTemplates = () =>
  useQuery(
    ["survey-templates"],
    () => fetchData("/surveys/templates", "GET"),
    {
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      select: (response) => response.data || [],
    }
  );

export const useSurveyDetails = (surveyId, enabled = true) =>
  useQuery(
    ["survey-details", surveyId],
    () => fetchData(`/surveys/${surveyId}`, "GET"),
    {
      enabled: Boolean(surveyId) && enabled,
      refetchOnWindowFocus: false,
      select: (response) => response.data || null,
    }
  );

async function fetchPlatformSurveyAnalyticsPreview(platformSurveyKey, filters) {
  const previewRes = await fetchPlatformSurveyAnalyticsDeferred(platformSurveyKey, {
    ...filters,
    preview: 1,
  });
  if (previewRes.httpStatus === 200 && previewRes.data) {
    return previewRes.data;
  }
  return null;
}

export const useSurveyAnalytics = (surveyId, enabled = true, filters = {}) => {
  const consolidatedCtx = useConsolidatedDashboardContext();
  const consolidatedSurveys = Boolean(consolidatedCtx?.surveysEnabled);
  const isPlatform = consolidatedCtx?.variant === "platform";
  const analyticsPrefix = isPlatform ? "platform-survey-analytics" : "partner-survey-analytics";
  const surveyKey = String(surveyId || "").trim();
  const isConsolidatedKey = surveyKey.includes(":");
  const bootstrapJobId = useDashboardJobBootstrap();
  const registerBackgroundJob = useDashboardJobsRegister();
  const { isJobCancelled, isScopeCancelled, hasActiveBackgroundJobs } = useDashboardBackgroundJobs();

  const scopeKey = buildDashboardScopeKey(
    "surveys",
    `analytics:${surveyKey}`,
    { start: filters.startDate, end: filters.endDate },
    consolidatedSurveys ? consolidatedCtx?.dominioIds : []
  );
  const scopeCancelled = isScopeCancelled(scopeKey);

  const key = consolidatedSurveys
    ? [analyticsPrefix, surveyKey, filters, bootstrapJobId || null]
    : ["survey-analytics", surveyId, filters, bootstrapJobId || null];

  const consolidatedWorkspaceCount = consolidatedSurveys
    ? consolidatedCtx?.dominioIds?.length || consolidatedCtx?.workspaces?.length || 0
    : 0;

  const prefetchPreview =
    !scopeCancelled &&
    (consolidatedSurveys
      ? shouldPrefetchPartnerDashboardPreview(
          filters.startDate,
          filters.endDate,
          consolidatedWorkspaceCount
        )
      : shouldPrefetchDashboardPreview(filters.startDate, filters.endDate));

  const previewKey = consolidatedSurveys
    ? [`${analyticsPrefix}-preview`, surveyKey, filters]
    : ["survey-analytics-preview", surveyId, filters];

  const query = useQuery(
    key,
    async () => {
      if (scopeCancelled) {
        return { data: null, deferred: false, jobMeta: null, userCancelled: true, scopeKey };
      }

      if (consolidatedSurveys && isConsolidatedKey) {
        if (bootstrapJobId) {
          const boot = await bootstrapPartnerSurveyJob(bootstrapJobId, surveyKey, {
            variant: isPlatform ? "platform" : "partner",
          });
          if (boot.kind === "ready") {
            return { data: boot.payload, deferred: false, jobMeta: null, scopeKey };
          }
          if (boot.kind === "pending") {
            return {
              data: null,
              deferred: true,
              jobMeta: {
                jobId: boot.jobId,
                pollAfterMs: boot.pollAfterMs,
                status: "computing",
              },
              scopeKey,
            };
          }
        }

        const fetchDeferred = isPlatform
          ? fetchPlatformSurveyAnalyticsDeferred
          : fetchPartnerSurveyAnalyticsDeferred;
        const first = await fetchDeferred(surveyKey, filters);
        const firstBody = first?.data ?? first;
        if (first.httpStatus === 202 || isDeferredDashboardMeta(firstBody)) {
          const meta = isDeferredDashboardMeta(firstBody) ? firstBody : firstBody;
          if (meta?.jobId && isJobCancelled(meta.jobId)) {
            return { data: null, deferred: false, jobMeta: null, userCancelled: true, scopeKey };
          }
          return { data: null, deferred: true, jobMeta: meta, scopeKey };
        }
        return { data: firstBody, deferred: false, jobMeta: null, scopeKey };
      }

      if (bootstrapJobId) {
        const boot = await bootstrapSurveyJob(bootstrapJobId, surveyId);
        if (boot.kind === "ready") {
          return { data: boot.payload, deferred: false, jobMeta: null, scopeKey };
        }
        if (boot.kind === "pending") {
          return {
            data: null,
            deferred: true,
            jobMeta: {
              jobId: boot.jobId,
              pollAfterMs: boot.pollAfterMs,
              status: "computing",
            },
            scopeKey,
          };
        }
      }

      const params = new URLSearchParams();
      if (filters.startDate) params.set("startDate", filters.startDate);
      if (filters.endDate) params.set("endDate", filters.endDate);
      if (filters.sourceLabel) params.set("sourceLabel", filters.sourceLabel);
      if (filters.channel) params.set("channel", filters.channel);
      if (filters.originId) params.set("originId", String(filters.originId));
      const queryStr = params.toString();
      const url = `/surveys/${surveyId}/analytics${queryStr ? `?${queryStr}` : ""}`;
      const first = await fetchDashboardDeferred(url);
      const firstBody = first?.data ?? first;
      if (first.httpStatus === 202 || isDeferredDashboardMeta(firstBody)) {
        const meta = isDeferredDashboardMeta(firstBody) ? firstBody : firstBody;
        if (meta?.jobId && isJobCancelled(meta.jobId)) {
          return { data: null, deferred: false, jobMeta: null, userCancelled: true, scopeKey };
        }
        return { data: null, deferred: true, jobMeta: meta, scopeKey };
      }
      return { data: firstBody, deferred: false, jobMeta: null, scopeKey };
    },
    {
      enabled: Boolean(surveyKey) && enabled && (!consolidatedSurveys || isConsolidatedKey) && !scopeCancelled,
      staleTime: 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  useEffect(() => {
    const jobMeta = query.data?.jobMeta;
    if (!query.data?.deferred || !jobMeta?.jobId || query.isPreviousData) return;
    if (isJobCancelled(jobMeta.jobId)) return;
    registerBackgroundJob(jobMeta, {
      start: filters.startDate,
      end: filters.endDate,
      title: dashboardRouteLabel(jobMeta.route),
    });
  }, [
    query.data?.deferred,
    query.data?.jobMeta,
    query.isPreviousData,
    filters.startDate,
    filters.endDate,
    registerBackgroundJob,
    isJobCancelled,
  ]);

  const isPreviousData = Boolean(query.isPreviousData);
  const userCancelled = Boolean(query.data?.userCancelled) || scopeCancelled;
  const isComputing = Boolean(query.data?.deferred) && !isPreviousData && !userCancelled;
  const shouldLoadPreview = !scopeCancelled && (prefetchPreview || isComputing);

  const previewQuery = useQuery(
    previewKey,
    () =>
      consolidatedSurveys && isConsolidatedKey
        ? (isPlatform ? fetchPlatformSurveyAnalyticsPreview : fetchPartnerSurveyAnalyticsPreview)(
            surveyKey,
            filters
          )
        : fetchSurveyAnalyticsPreview(surveyId, filters),
    {
      enabled:
        shouldLoadPreview &&
        Boolean(surveyKey) &&
        enabled &&
        (!consolidatedSurveys || isConsolidatedKey),
      staleTime: 60_000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const isBackgroundMode = isComputing && hasActiveBackgroundJobs;
  const mainPending =
    query.isLoading || (query.isFetching && !query.data?.data && !isComputing && !isBackgroundMode);

  const fullData = !isPreviousData && query.data?.data && !isComputing ? query.data.data : null;
  const previewRaw = shouldLoadPreview && previewQuery.data ? previewQuery.data : null;
  const usePreview = !fullData && Boolean(previewRaw);

  const displayData = isPreviousData ? null : fullData ?? (usePreview ? previewRaw : null);
  const isPreview = usePreview && !fullData;
  const previewPending =
    shouldLoadPreview &&
    displayData == null &&
    (previewQuery.isLoading || previewQuery.isFetching) &&
    !previewQuery.isError;
  const awaitingPreview = previewPending;

  return {
    ...query,
    data: displayData,
    error: query.error || (!isPreviousData && query.data?.pollError) || null,
    isDeferred: isComputing && !isBackgroundMode,
    isComputing,
    isBackgroundJob: isBackgroundMode,
    isPreview,
    isAwaitingPreview: awaitingPreview,
    userCancelled,
    isLoading:
      displayData == null &&
      !userCancelled &&
      (awaitingPreview ||
        (!isComputing && !isBackgroundMode && mainPending) ||
        isPreviousData),
    isFetchingFull:
      !isBackgroundMode &&
      !userCancelled &&
      (mainPending && Boolean(isPreview)),
  };
};

export const useSurveyResponses = (
  surveyId,
  page = 1,
  limit = 20,
  search = "",
  enabled = true,
  filters = {}
) =>
  useQuery(
    ["survey-responses", surveyId, page, limit, search, filters],
    () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) params.set("search", search);
      if (filters.startDate) params.set("startDate", filters.startDate);
      if (filters.endDate) params.set("endDate", filters.endDate);
      if (filters.sourceLabel) params.set("sourceLabel", filters.sourceLabel);
      if (filters.channel) params.set("channel", filters.channel);
      if (filters.originId) params.set("originId", String(filters.originId));
      return fetchData(`/surveys/${surveyId}/responses?${params.toString()}`);
    },
    {
      enabled: Boolean(surveyId) && enabled,
      staleTime: 30 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      select: (response) => ({
        sessions: response.data || [],
        pagination: response.pagination || { total: 0, page: 1, totalPages: 1, limit },
      }),
    }
  );

export const usePublicSurvey = (slug, company, token) =>
  useQuery(
    ["public-survey", slug, company, token],
    () => fetchPublicSurvey(slug, company, token),
    {
      enabled: Boolean(slug && company),
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      select: (response) => response.data || null,
    }
  );

export const usePublicSurveySession = () =>
  useMutation(({ slug, company, payload }) => startPublicSurveySession(slug, company, payload));

export const useSubmitPublicSurveyAnswers = () =>
  useMutation(({ slug, company, token, answers }) =>
    submitPublicSurveyAnswers(slug, company, token, answers)
  );
